import {Injectable, Injector, runInInjectionContext} from '@angular/core';
import {
  Firestore,
  CollectionReference,
  collection,
  collectionData,
  doc,
  docData,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import {getDownloadURL, getStorage, ref} from 'firebase/storage';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {
  EvaluationComment,
  EvaluationDecision,
  EvaluationGame,
  EvaluationSeason,
  Producer,
  Reward,
} from '../model/evaluation/evaluation';
import {hasRole, isEvaluationAdmin, User} from '../model/user';
import {AuthService} from './auth.service';

/** States a producer is allowed to see at all. */
export const PRODUCER_VISIBLE_STATES = ['toApprove', 'returned', 'approved'];

/** Where the chosen season is remembered. See `selectSeason`. */
const SEASON_KEY = 'ballscore.evaluation.season';

/**
 * Both admin answers to a declaration, wiped. A producer who changes their
 * mind invalidates whatever was said about the old answer, and leaving either
 * behind would show a confirmation of something nobody confirmed.
 */
const CLEAR_ANSWERS = {
  'declarations.confirmedBy': null,
  'declarations.confirmedByName': null,
  'declarations.confirmedAt': null,
  'declarations.disputedBy': null,
  'declarations.disputedByName': null,
  'declarations.disputedAt': null,
  'declarations.disputeNote': null,
};

/**
 * Reads and writes the evaluation workflow.
 *
 * Everything here writes the **decision layer** only. The measured layer
 * (`measurements`, `facts`, `events` and the match identity) belongs to
 * `sle publish` and a write from the web would be overwritten on the next run
 * at best, or silently disagree with the tool at worst.
 */
@Injectable({
  providedIn: 'root'
})
export class EvaluationsService {

  private seasonsRef: CollectionReference;
  private storage = getStorage();
  /** Which season the app is looking at; resolved from Firestore on first use. */
  private readonly season$$ = new BehaviorSubject<string | null>(null);
  private readonly seasons$$ = new BehaviorSubject<string[]>([]);
  private evidenceUrls = new Map<string, Promise<string>>();

  constructor(
    private afs: Firestore,
    private auth: AuthService,
    private injector: Injector,
  ) {
    this.seasonsRef = collection(this.afs, 'evaluations');
    this.watchSeasons();
  }

  /**
   * Season ids, newest first, kept live.
   *
   * Live rather than fetched once because the next season appears the moment
   * the tool first publishes into it, and a season nobody can see is a season
   * nobody can work on until they think to reload.
   */
  seasons(): Observable<string[]> {
    return this.seasons$$.asObservable();
  }

  /** The season the UI works with. Defaults to the newest that exists. */
  currentSeason(): Observable<string> {
    return this.season$$.asObservable().pipe(map(season => season ?? ''));
  }

  /**
   * Look at another season.
   *
   * Remembered across reloads, because the reason to be in an old season is
   * invoicing or a dispute — work that takes more than one page load, and
   * being thrown back to the newest one each time would make it unusable.
   * The sidebar always names the season in view and says when it is not the
   * current one, so the choice cannot be quietly forgotten.
   */
  selectSeason(season: string): void {
    this.season$$.next(season);
    this.remember(season);
  }

  /** The newest season there is, for telling "old" from "current" apart. */
  get latestSeason(): string | null {
    return this.seasons$$.value[0] ?? null;
  }

  /**
   * **The app never moves anybody between seasons by itself.**
   *
   * That rule is worth stating because the obvious implementation breaks it.
   * Defaulting to the newest season sounds harmless until the moment it
   * matters: `sle publish` writes the new season's rate card before it has
   * finished writing its games, so for as long as that takes, "the newest
   * season" is an empty one — and everybody who reloaded would be looking at
   * it instead of the season they were working in, with last season's
   * invoicing apparently gone.
   *
   * So the season is remembered from the first time it is resolved, not only
   * when somebody picks one, and a new season changes nothing until it is
   * chosen. What a new season does do is make the sidebar say *Not the
   * current season*, which is the prompt to move when the work is ready.
   */
  private watchSeasons(): void {
    (collectionData(this.seasonsRef, {idField: 'season'}) as Observable<{ season: string }[]>)
      .pipe(map(docs => docs.map(d => d.season).sort().reverse()))
      .subscribe(seasons => {
        this.seasons$$.next(seasons);
        if (!seasons.length || this.season$$.value) {
          return;
        }
        // A remembered season that no longer exists must not strand the app on
        // an empty screen; the newest one is the answer for a first visit.
        const remembered = this.remembered();
        const resolved = remembered && seasons.includes(remembered) ? remembered : seasons[0];
        this.season$$.next(resolved);
        this.remember(resolved);
      });
  }

  private remembered(): string | null {
    try {
      return localStorage.getItem(SEASON_KEY);
    } catch (e) {
      return null;
    }
  }

  private remember(season: string): void {
    try {
      localStorage.setItem(SEASON_KEY, season);
    } catch (e) {
      // Private windows and locked-down browsers: the choice holds for this
      // page, which is better than refusing to switch at all.
    }
  }

  /** The rate card and criterion definitions. Published by the tool. */
  season(): Observable<EvaluationSeason | null> {
    return this.season$$.pipe(
      switchMap(season => season
        ? docData(doc(this.seasonsRef, season)) as Observable<EvaluationSeason>
        : of(null))
    );
  }

  private gamesRef(season: string): CollectionReference {
    return collection(this.afs, `evaluations/${season}/games`);
  }

  /** Every game of the season, oldest first. Admin only — rules see to that. */
  games(): Observable<EvaluationGame[]> {
    return this.season$$.pipe(
      switchMap(season => season
        ? collectionData(query(this.gamesRef(season), orderBy('date'))) as Observable<EvaluationGame[]>
        : of([]))
    );
  }

  /** The games one producer is allowed to see. */
  producerGames(channelId: number): Observable<EvaluationGame[]> {
    return this.season$$.pipe(
      switchMap(season => season
        ? collectionData(query(this.gamesRef(season),
          where('channelId', '==', channelId),
          where('state', 'in', PRODUCER_VISIBLE_STATES))) as Observable<EvaluationGame[]>
        : of([])),
      map(games => [...games].sort((a, b) => a.date.localeCompare(b.date)))
    );
  }

  game(videoId: number | string): Observable<EvaluationGame | null> {
    return this.season$$.pipe(
      switchMap(season => season
        ? docData(doc(this.gamesRef(season), String(videoId))) as Observable<EvaluationGame>
        : of(null))
    );
  }

  decisions(videoId: number | string): Observable<EvaluationDecision[]> {
    return this.season$$.pipe(
      switchMap(season => season
        ? collectionData(collection(this.afs,
          `evaluations/${season}/games/${videoId}/decisions`)) as Observable<EvaluationDecision[]>
        : of([]))
    );
  }

  comments(videoId: number | string): Observable<EvaluationComment[]> {
    return this.season$$.pipe(
      switchMap(season => season
        ? collectionData(collection(this.afs,
          `evaluations/${season}/games/${videoId}/comments`), {idField: 'id'}) as Observable<EvaluationComment[]>
        : of([])),
      map(comments => [...comments].sort((a, b) => this.millis(a.at) - this.millis(b.at)))
    );
  }

  producers(): Observable<Producer[]> {
    return collectionData(collection(this.afs, 'producers')) as Observable<Producer[]>;
  }

  /**
   * The two subcollections of one game, read once.
   *
   * The live versions above are what the screens use; the export is not a
   * screen. It opens every approved broadcast of a production — forty-odd
   * games, two subcollections each — and `collectionData` would leave eighty
   * snapshot listeners attached to a page that is already finished with them.
   */
  async decisionsOnce(videoId: number | string): Promise<EvaluationDecision[]> {
    const snapshot = await this.fetch(`${videoId}/decisions`);
    return snapshot.docs.map(entry => entry.data() as EvaluationDecision);
  }

  async commentsOnce(videoId: number | string): Promise<EvaluationComment[]> {
    const snapshot = await this.fetch(`${videoId}/comments`);
    return snapshot.docs
      .map(entry => ({id: entry.id, ...entry.data()}) as EvaluationComment)
      .sort((a, b) => this.millis(a.at) - this.millis(b.at));
  }

  /**
   * A one-shot read, inside the injection context.
   *
   * `runInInjectionContext` is not decoration: it is how `@angular/fire` keeps
   * Zone aware of the request. A `getDocs` called outside it leaves Angular
   * with no reason to run change detection when the promise settles, so the
   * button that started the export would keep its spinner after the file had
   * already been handed to the browser. Dev mode says so in the console; in a
   * production build it is simply a UI that stops updating.
   */
  private fetch(path: string): Promise<any> {
    const season = this.requireSeason();
    return runInInjectionContext(this.injector, () =>
      getDocs(collection(this.afs, `evaluations/${season}/games/${path}`)));
  }

  // --- writes --------------------------------------------------------------

  /**
   * An admin rules on a criterion the tool would not decide.
   *
   * Two writes on purpose: the ruling itself with who and why, and the value
   * folded into the game document. Without the second one every list would
   * have to open a subcollection per game to know whether the game is settled.
   */
  async decide(videoId: number | string, criterion: string,
               value: number | null, note: string, original: number | null): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await setDoc(doc(this.afs, `evaluations/${season}/games/${videoId}/decisions/${criterion}`), {
      criterion,
      value,
      note: note ?? '',
      by: user?.uid ?? null,
      byName: user?.displayName ?? user?.email ?? null,
      at: serverTimestamp(),
      original: original ?? null,
    });
    await updateDoc(doc(this.gamesRef(season), String(videoId)), {
      [`values.${criterion}`]: value,
      valuesAt: serverTimestamp(),
    });
  }

  /**
   * Hands a batch of games to their producers.
   *
   * Deliberately explicit rather than automatic: this is the moment the numbers
   * stop being internal, and it is about money.
   */
  async release(games: EvaluationGame[]): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    const batch = writeBatch(this.afs);
    games.forEach(game => {
      batch.update(doc(this.gamesRef(season), String(game.videoId)), {
        state: 'toApprove',
        releasedAt: serverTimestamp(),
        releasedBy: user?.uid ?? null,
      });
    });
    await batch.commit();
  }

  /**
   * An admin takes a game back from the producer to re-rule it.
   *
   * Only while nothing is approved: an approved game carries an agreed amount,
   * and reopening that is a conversation, not a button. The rulings already
   * made stay — the point is to fix one of them, not to start over — and so
   * does the release stamp, which the next release overwrites anyway.
   *
   * It also unblocks `sle publish`: the tool refuses to overwrite the measured
   * layer of anything the producer has seen, so a re-measured verdict only
   * reaches the game once it is back in `toDecide`.
   */
  async withdraw(videoId: number | string): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await updateDoc(doc(this.gamesRef(season), String(videoId)), {
      state: 'toDecide',
      withdrawnAt: serverTimestamp(),
      withdrawnBy: user?.uid ?? null,
      withdrawnByName: user?.displayName ?? user?.email ?? null,
    });
  }

  /** The producer says what kind of operated camera the game had. */
  async declareOperatedCamera(videoId: number | string, value: number): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await updateDoc(doc(this.gamesRef(season), String(videoId)), {
      'declarations.operatedCamera': value,
      'declarations.declaredBy': user?.uid ?? null,
      'declarations.declaredByName': user?.displayName ?? user?.email ?? null,
      'declarations.declaredAt': serverTimestamp(),
      ...CLEAR_ANSWERS,
    });
  }

  /**
   * An admin puts their name to a claim worth money.
   *
   * One game at a time on purpose. There was briefly a bulk version, for
   * sweeping up declarations of *none* — until the simpler answer turned up:
   * a none needs no confirming at all, so the list it was meant to clear does
   * not exist.
   *
   * Only these fields are written, and never the amount: the reward already
   * followed the declaration when the producer approved, so confirming is a
   * record that somebody looked. Turning it into a condition of payment would
   * restate figures that have been agreed. It works in every state,
   * `approved` included, because that is where nearly all declarations sit.
   */
  async confirmDeclaration(videoId: number | string): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await updateDoc(doc(this.gamesRef(season), String(videoId)), {
      'declarations.confirmedBy': user?.uid ?? null,
      'declarations.confirmedByName': user?.displayName ?? user?.email ?? null,
      'declarations.confirmedAt': serverTimestamp(),
      // Confirming after a dispute is how a settled argument is recorded;
      // leaving the dispute behind would keep the game on a chase list
      // nobody can clear.
      'declarations.disputedBy': null,
      'declarations.disputedByName': null,
      'declarations.disputedAt': null,
      'declarations.disputeNote': null,
    });
  }

  /**
   * An admin does not accept what the producer declared.
   *
   * It moves no money, and that is the point. On an approved game the amount
   * is agreed; rewriting the declaration would leave the frozen `reward`
   * standing on a number that no longer holds, which is the one thing this
   * workflow exists to prevent. So a dispute is a record plus a comment —
   * posted to the same thread every other objection on this game lives in, so
   * the producer reads it where they already look — and the correction is a
   * conversation. Where the producer has *not* approved yet there is a real
   * fix and the screen says so: take the game back and let them declare again.
   *
   * The comment goes first. If it fails there is no dispute anybody can see,
   * which is better than a flag on the game that reaches nobody.
   */
  async disputeDeclaration(videoId: number | string, note: string): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await this.addComment(videoId, 'operatedCamera', note);
    await updateDoc(doc(this.gamesRef(season), String(videoId)), {
      'declarations.disputedBy': user?.uid ?? null,
      'declarations.disputedByName': user?.displayName ?? user?.email ?? null,
      'declarations.disputedAt': serverTimestamp(),
      'declarations.disputeNote': note,
      'declarations.confirmedBy': null,
      'declarations.confirmedByName': null,
      'declarations.confirmedAt': null,
    });
  }

  /** The producer accepts the evaluation, and with it the amount. */
  async approve(videoId: number | string, reward: Reward): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await updateDoc(doc(this.gamesRef(season), String(videoId)), {
      state: 'approved',
      approvedAt: serverTimestamp(),
      approvedBy: user?.uid ?? null,
      approvedByName: user?.displayName ?? user?.email ?? null,
      // The amount is frozen as approved, together with the rates it came from:
      // a later rate card must not silently restate what was agreed.
      reward: {...reward, computedAt: serverTimestamp()},
    });
  }

  /** The producer disagrees; the note says with what. */
  async returnGame(videoId: number | string, criterion: string | null, text: string): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await this.addComment(videoId, criterion, text);
    await updateDoc(doc(this.gamesRef(season), String(videoId)), {
      state: 'returned',
      returnedAt: serverTimestamp(),
      returnedBy: user?.uid ?? null,
    });
  }

  async addComment(videoId: number | string, criterion: string | null, text: string): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    const commentsRef = collection(this.afs, `evaluations/${season}/games/${videoId}/comments`);
    await setDoc(doc(commentsRef), {
      criterion,
      text,
      by: user?.uid ?? null,
      byName: user?.displayName ?? user?.email ?? null,
      at: serverTimestamp(),
    });
  }

  async resolveComment(videoId: number | string, commentId: string): Promise<void> {
    const season = this.requireSeason();
    const user = this.auth.user;
    await updateDoc(doc(this.afs,
      `evaluations/${season}/games/${videoId}/comments/${commentId}`), {
      resolvedBy: user?.uid ?? null,
      resolvedAt: serverTimestamp(),
    });
  }

  // --- evidence ------------------------------------------------------------

  /**
   * Download URL for a still. Cached: a wizard screen asks for the same handful
   * of paths over and over as the admin walks the list.
   */
  /**
   * The recording on this machine, or a rejection when it is not here.
   *
   * The URL carries a Firebase ID token as a query parameter, because a
   * `<video src>` cannot send an Authorization header; the server verifies it
   * and serves admins only. A HEAD request first, so the page shows no player
   * where the file does not exist — which is everywhere but the analyser's
   * own machine.
   */
  async recordingUrl(videoId: number, at: number | null = null): Promise<string> {
    const token = await this.auth.idToken();
    if (!token) {
      throw new Error('not signed in');
    }
    const url = `/local-video/${videoId}?token=${encodeURIComponent(token)}`;
    const probe = await fetch(url, {method: 'HEAD'});
    if (!probe.ok) {
      throw new Error(`recording unavailable (${probe.status})`);
    }
    // A server without the route answers with the SPA fallback — 200 and an
    // HTML page. Pointing a <video> at that shows a broken player and no
    // reason why, so the content type decides, not the status.
    if (!(probe.headers.get('content-type') || '').startsWith('video/')) {
      throw new Error('recording endpoint not available on this server');
    }
    return at ? `${url}#t=${Math.max(0, Math.floor(at))}` : url;
  }

  evidenceUrl(path: string): Promise<string> {
    if (!this.evidenceUrls.has(path)) {
      this.evidenceUrls.set(path, getDownloadURL(ref(this.storage, path)));
    }
    return this.evidenceUrls.get(path);
  }

  // --- helpers -------------------------------------------------------------

  /** Whoever runs the workflow: a full admin, or an evaluation admin. */
  isAdmin(user: User): boolean {
    return isEvaluationAdmin(user);
  }

  isProducer(user: User): boolean {
    return hasRole(user, 'producer') && !!user?.channelId;
  }

  private requireSeason(): string {
    const season = this.season$$.value;
    if (!season) {
      throw new Error('No evaluation season is loaded yet.');
    }
    return season;
  }

  private millis(value: any): number {
    if (!value) {
      return 0;
    }
    return typeof value.toMillis === 'function' ? value.toMillis() : new Date(value).getTime();
  }
}
